import { useState, useEffect } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { getCategoryStyle } from "@/features/assets/services/assetCategories";

export interface AssetPhoto {
  id: string;
  file_path: string;
  document_name: string;
}

export interface Asset {
  id: string;
  property_id?: string | null;
  name: string;
  manufacturer?: string;
  serial_number?: string;
  status: string;
  asset_type?: string;
  manufacture_date?: string | null;
  manufacture_date_string?: string | null;
  installation_date?: string | null;
  estimated_age_years?: number | null;
  age_source?: string | null;
  category?: string;
  photos: AssetPhoto[];

  // New schema fields
  category_id?: string | null;
  category_attributes?: any;
  model_number?: string | null;
  model_name?: string | null;
  condition_status?: string | null;
  asset_photo_url?: string | null;
  nameplate_photo_url?: string | null;

  // Pipeline + AI-extracted fields (migration 20260424000001)
  processing_status?: string | null;
  condition?: string | null;
  refrigerant_type?: string | null;
  size_tons?: number | null;

  // Nameplate
  voltage?: string | null;
  amperage?: string | null;
  phase?: string | null;
  wires?: string | null;
  btu_capacity?: string | null;
  capacity_gallons?: number | null;
  pressure_psi?: number | null;
  frequency_hz?: number | null;
  power_kw?: number | null;
  efficiency_rating?: string | null;
  fuel_type?: string | null;
  airflow_cfm?: number | null;
  gas_cfh_low_diff?: number | null;
  gas_cfh_high_diff?: number | null;

  // Pipeline audit
  ocr_confidence?: number | null;
  ocr_completed_at?: string | null;
  ocr_source?: string | null;
  ai_confidence?: number | null;
  ai_completed_at?: string | null;
  ai_source?: string | null;
  ai_model_used?: string | null;
  ai_failure_reason?: string | null;

  // AI recommendations
  ai_suggested_rul_months?: number | null;
  ai_suggested_condition?: string | null;
  ai_recommendations?: unknown;
  ai_verified_model?: boolean | null;
  ai_verified_serial?: boolean | null;
  ai_verified_manufacturer?: boolean | null;

  // QR code
  qr_code_id?: string | null;

  notes?: string | null;
}

export interface CategoryWithAssets {
  id: string;
  name: string;
  icon_name: string;
  color: string;
  assets: Asset[];
  assetCount: number;
}

export function usePropertyAssetRegistry(propertyId: string) {
  const [categories, setCategories] = useState<CategoryWithAssets[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchAssets = async () => {
    if (!propertyId) return;

    try {
      setLoading(true);

      // Fetch assets using the new category schema columns and relationships
      // Also select legacy `category` column for fallback on old properties
      const { data: assetsData, error: assetsError } = await supabase
        .from('property_assets')
        .select(`
          id,
          name,
          manufacturer,
          model_number,
          model_name,
          serial_number,
          status,
          category_id,
          asset_categories (
            name
          ),
          asset_photo_url,
          nameplate_photo_url,
          manufacture_date_string,
          estimated_age_years,
          age_source,
          category_attributes,
          processing_status,
          condition_status,
          ai_failure_reason,
          qr_code_id
        `)
        .eq('property_id', propertyId)
        .order('name', { ascending: true });

      if (assetsError) throw assetsError;

      let photosData: any[] = [];

      if (assetsData && assetsData.length > 0) {
        // Fetch photos using asset IDs in batches (no FK relationship exists for asset_documents)
        const assetIds = assetsData.map(a => a.id);
        const batchSize = 100; // Avoid 414 URI Too Long errors
        
        for (let i = 0; i < assetIds.length; i += batchSize) {
          const batchIds = assetIds.slice(i, i + batchSize);
          
          const { data: docsData, error: photosError } = await supabase
            .from('asset_documents')
            .select('id, asset_id, file_path, document_name')
            .is('deleted_at', null)
            .eq('document_type', 'photo')
            .in('asset_id', batchIds);
          
          if (photosError) {
            console.error('Error fetching photos batch:', photosError);
          } else if (docsData) {
            photosData.push(...docsData);
          }
        }
      }

      // Group assets by category
      const categoryGroups = new Map<string, Asset[]>();

      (assetsData || []).forEach(asset => {
        // New schema: asset_categories join → Legacy: category text column → Fallback
        const categoryName = (asset.asset_categories as any)?.name || 'Uncategorized';
        
        // Get photos from asset_documents
        const documentPhotos = photosData
          .filter(photo => photo.asset_id === asset.id)
          .map(({ asset_id, ...photo }) => photo);
        
        // Build photos array: use asset_documents as source of truth
        // Fall back to asset_photo_url, nameplate_photo_url or legacy primary_image_path
        const photos: AssetPhoto[] = [];
        const fallbackUrl = asset.asset_photo_url || asset.nameplate_photo_url;
        
        if (documentPhotos.length > 0) {
          photos.push(...documentPhotos);
        } else if (fallbackUrl) {
          photos.push({
            id: `primary-${asset.id}`,
            file_path: fallbackUrl,
            document_name: 'Primary Image'
          });
        }
        
        const attrs = (asset.category_attributes as any) || {};

        const assetWithDetails = {
          ...asset,
          category: categoryName,
          asset_type: categoryName,
          condition: asset.condition_status || 'Good',

          // Map specs from category_attributes JSONB
          voltage: attrs.voltage?.toString() || null,
          amperage: attrs.amperage?.toString() || null,
          phase: attrs.phase || null,
          wires: attrs.wires || null,
          btu_capacity: attrs.btu_capacity?.toString() || null,
          capacity_gallons: attrs.gallon_capacity != null ? Number(attrs.gallon_capacity) : null,
          pressure_psi: attrs.pressure_psi != null ? Number(attrs.pressure_psi) : null,
          frequency_hz: attrs.frequency_hz != null ? Number(attrs.frequency_hz) : null,
          power_kw: attrs.power_kw != null ? Number(attrs.power_kw) : null,
          efficiency_rating: attrs.seer_eer?.toString() || attrs.efficiency_rating?.toString() || null,
          fuel_type: attrs.fuel_type || attrs.generator_fuel_type || null,
          airflow_cfm: attrs.airflow_cfm != null ? Number(attrs.airflow_cfm) : null,
          refrigerant_type: attrs.refrigerant_type || null,
          size_tons: attrs.tonnage != null ? Number(attrs.tonnage) : null,

          photos,
        };

        if (!categoryGroups.has(categoryName)) {
          categoryGroups.set(categoryName, []);
        }
        categoryGroups.get(categoryName)!.push(assetWithDetails as unknown as Asset);
      });

      // Convert to CategoryWithAssets array with styling from system config
      const categoriesWithAssets: CategoryWithAssets[] = Array.from(categoryGroups.entries())
        .map(([categoryName, assets]) => {
          const style = getCategoryStyle(categoryName);
          
          return {
            id: categoryName.toLowerCase().replace(/\s+/g, '-'),
            name: categoryName,
            icon_name: style.icon.displayName || 'Package',
            color: style.color,
            assets,
            assetCount: assets.length,
          };
        })
        // Sort: Uncategorized last, then alphabetically
        .sort((a, b) => {
          if (a.name === 'Uncategorized') return 1;
          if (b.name === 'Uncategorized') return -1;
          return a.name.localeCompare(b.name);
        });

      setCategories(categoriesWithAssets);
    } catch (error) {
      console.error('Error fetching asset registry:', error);
      toast({
        title: "Error",
        description: "Failed to load asset registry",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [propertyId]);

  return {
    categories,
    loading,
    refetch: fetchAssets,
  };
}
